import { motion } from 'framer-motion';
import Navbar from './Navbar';
import Footer from './Footer';
import { Shield } from 'lucide-react';
import SEOHead from './SEOHead';

/**
 * Shared layout wrapper for legal/policy pages (Privacy, Terms, Cookies,
 * Fair Housing, etc.). Keeps the reading typography consistent — max 72ch
 * line length, generous spacing, and a sticky table-of-contents on desktop.
 */
export default function LegalPage({
  icon: Icon = Shield,
  eyebrow = 'Legal',
  title,
  effectiveDate,
  summary,
  sections = [],
  children,
}) {
  return (
    <div className="min-h-screen bg-cream-100" data-testid="legal-page">
      <SEOHead title={`${title} · RoomHora`} description={summary} />
      <Navbar />

      {/* Hero band */}
      <header className="border-b border-slate-200/70 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 pt-28 pb-12">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
          >
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
              <Icon className="w-3.5 h-3.5" />
              {eyebrow}
            </div>
            <h1 className="mt-3 font-serif text-4xl md:text-5xl lg:text-[3.5rem] leading-[1.05] tracking-[-0.02em] text-slate-900">
              {title}
            </h1>
            {effectiveDate && (
              <p className="mt-4 text-sm text-slate-500">
                Effective {effectiveDate}
              </p>
            )}
            {summary && (
              <p className="mt-5 max-w-2xl text-base md:text-lg leading-relaxed text-slate-600">
                {summary}
              </p>
            )}
          </motion.div>
        </div>
      </header>

      {/* Body */}
      <main className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-[220px_minmax(0,1fr)] gap-10">
          {/* Sticky TOC (desktop only) */}
          {sections.length > 0 && (
            <nav
              aria-label="On this page"
              className="hidden lg:block sticky top-28 self-start text-sm"
            >
              <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-slate-500 mb-3">
                On this page
              </div>
              <ul className="space-y-2 border-l border-slate-200 pl-4">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="text-slate-600 hover:text-accent transition-colors"
                    >
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          )}

          {/* Content */}
          <article className="prose-legal max-w-none">
            {sections.map((s) => (
              <section
                key={s.id}
                id={s.id}
                className="mb-10 scroll-mt-28"
                data-testid={`legal-section-${s.id}`}
              >
                <h2 className="font-serif text-2xl md:text-[1.8rem] text-slate-900 tracking-[-0.01em] mb-4">
                  {s.title}
                </h2>
                <div className="space-y-4 text-[15px] md:text-base leading-[1.75] text-slate-700">
                  {s.body}
                </div>
              </section>
            ))}
            {children}

            <div className="mt-16 pt-8 border-t border-slate-200 text-sm text-slate-500">
              Questions about this policy?{' '}
              <a
                href="mailto:roomhora@gmail.com"
                className="text-accent font-medium hover:underline"
              >
                roomhora@gmail.com
              </a>
              {' · '}
              This is a template — we recommend consulting a qualified attorney
              licensed in your jurisdiction before relying on it.
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}
